import {TabProps, Tabs} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {VEHICLE_HISTORY_SERVICE_REQUEST_COLUMNS} from '../../../constants/vehicleHistoryServiceRequestColumns';
import {catchUnhandledDataGridActions} from '../../../utils/catchUnhandledDataGridActions';
import {openVehicleHistoryServiceRequest} from '../../../utils/openVehicleHistoryServiceRequest';
import {DmsDataGrid} from '../../DmsDataGrid/DmsDataGrid';
import {VehicleHistoryControlConfig} from '../types/VehicleHistoryControlConfig';

interface VehicleHistoryDialogProps extends VehicleHistoryControlConfig, RequiredTestIdProps {}

const SERVICE_REQUESTS_TAB = 'serviceRequests';
const WORK_ITEMS_TAB = 'workItems';
const GRID_HEIGHT = '52vh';

/**
 * The page sections mount the same grid codes, and eag-grid keys server-side presets by grid code
 * alone — a dedicated instance keeps the dialog from writing over a section grid's stored preset.
 */
const PRESET_INSTANCE_ID = 'vehicle-history-dialog';

export function VehicleHistoryDialog(props: VehicleHistoryDialogProps) {
  const [activeTabId, setActiveTabId] = useState<string>(SERVICE_REQUESTS_TAB);

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {vehicleId: props.vehicleId}]);

  const openServiceRequestCallback: ActionCallback<
    typeof VEHICLE_HISTORY_SERVICE_REQUEST_COLUMNS
  > = ({actionKey, getColumnData, rowId}) =>
    match(actionKey)
      .with('open', () => {
        if (isArray(rowId)) {
          return;
        }

        openVehicleHistoryServiceRequest({
          serviceCaseId: getColumnData<string>('serviceCaseId')?.value,
          serviceOrderId: getColumnData<string>('serviceOrderId')?.value,
          serviceJobId: rowId,
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));

  const tabs: TabProps[] = [
    {
      id: SERVICE_REQUESTS_TAB,
      title: i18n.t('page.workshop.labels.serviceRequests'),
      content: (
        <Box height={GRID_HEIGHT}>
          <DmsDataGrid
            gridCode={props.serviceRequestsGridCode}
            predefinedColumns={VEHICLE_HISTORY_SERVICE_REQUEST_COLUMNS}
            actionCallback={props.serviceRequestsActionCallback ?? openServiceRequestCallback}
            queryModifier={queryModifier}
            presetInstanceId={PRESET_INSTANCE_ID}
            data-testid={suffixTestId('serviceRequests', props)}
          />
        </Box>
      ),
    },
    {
      id: WORK_ITEMS_TAB,
      title: i18n.t('entity.orderRequest.labels.workItems'),
      content: (
        <Box height={GRID_HEIGHT}>
          <DmsDataGrid
            gridCode={props.workItemsGridCode}
            actionCallback={props.workItemsActionCallback}
            queryModifier={queryModifier}
            presetInstanceId={PRESET_INSTANCE_ID}
            data-testid={suffixTestId('workItems', props)}
          />
        </Box>
      ),
    },
  ];

  return (
    <Tabs
      variant="condensed"
      tabs={tabs}
      activeTabId={activeTabId}
      onChange={setActiveTabId}
      isLazy
      lazyBehavior="unmount"
      data-testid={suffixTestId('tabs', props)}
    />
  );
}
